import React, { useEffect, useMemo, useState } from "react";
import clone from "lodash.clonedeep";
import toast, { Toaster } from "react-hot-toast";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Button, ButtonGroup, Col, Dropdown, Glyphicon, ListGroupItem, MenuItem, Row } from "react-bootstrap";
import { blockCreate, blockDelete, blocksOrderUpdate } from "../../../../../api/cp/pages";
import { useUserContext } from "../../../../context";
import locale from "../../../../locale";
import { COMMON_BLOCKS_LIST } from "../pages/models";
import BlockEditModal from "./BlockEditModal";
import MoveIcon from "../../Widgets/components/Icons/MoveIcon";
import DeleteConfirmOverlay from "../../../UI/DeleteConfirmOverlay";

const BLOCK_PLAYER = 'player';

const BlockSettings = ({ event, pageTemplate, blocks, pageId, options = [], updatePageSettings }) => {
    const { userIsAdmin, userIsSuperAdmin } = useUserContext();

    const [blocksList, setBlocksList] = useState([]);
    const [isEditBlockModalVisible, setEditBlockModalVisible] = useState(false);
    const [editableBlock, setEditableBlock] = useState(null);
    const [availableBlocksForAdmin, setAvailableBlocksForAdmin] = useState([]);

    const blockOptions = useMemo(() => {
        const optional = {
            shop: event.moduleShop?.enabled,
        };

        const _blockOptions = [...options, ...COMMON_BLOCKS_LIST.filter(({ template }) => !(template in optional) || optional[template])];

        return _blockOptions.filter(block =>
        (block.allowMultiple || !blocksList.find(({ template }) => block.template === template)
            && (!block.checkModule || event[block.checkModule]?.enabled))
        );

    }, [COMMON_BLOCKS_LIST, options, event, blocksList]);

    useEffect(() => {
        setBlocksList(blocks || []);
    }, [blocks]);

    useEffect(() => {
        const availableBlocks = blockOptions
            .filter((block) => block.hasOwnProperty('availableForAdmin'))
            .map(({ template }) => template);

        if (!!availableBlocks.length) {
            setAvailableBlocksForAdmin(availableBlocks);
        }
    }, [blockOptions]);

    async function handleAddBlock(template) {
        try {
            const response = await blockCreate(pageId, { template });
            setBlocksList((prevState) => ([...prevState, response]));
        } catch (error) {
            toast.error(locale('settings.error.blockAlreadyExists'), {
                duration: 4000,
            });
        }
    }

    async function handleUpdateBlocksOrder(list) {
        setBlocksList(list);

        try {
            const payload = list.map(({ id }) => id);
            const response = await blocksOrderUpdate(pageId, { order: payload });
            setBlocksList(response);
        } catch (error) {
            console.error(error);
        }
    }

    function onDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const reorderedBlocks = reorder(
            blocksList,
            result.source.index,
            result.destination.index,
        );

        handleUpdateBlocksOrder(reorderedBlocks);
    }

    function reorder(list, startIndex, endIndex) {
        const result = clone(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    }

    function handleSelectEditableBlock(block) {
        setEditableBlock(block);
        setEditBlockModalVisible(true);
    }

    function handleCloseModal() {
        setEditableBlock(null);
        setEditBlockModalVisible(false);
    }

    async function handleDeleteBlock(blockId) {
        await blockDelete(blockId);
        const list = blocksList.filter(({ id }) => id !== blockId);
        setBlocksList(list);
    }

    const updateBlock = (block) => {
        setBlocksList(list => list.map(item => item?.id === block?.id ? block : item));
        if (editableBlock?.id === block?.id) {
            setEditableBlock(block);
        }
    }

    const isBlockAvailableForUser = (template) => {
        return !availableBlocksForAdmin.includes(template) || (userIsAdmin || userIsSuperAdmin);
    };

    return (
        <>
            <Row>
                <Col xs={12}>
                    <Dropdown
                        id="add-broadcast-block"
                        disabled={!blockOptions.length}
                    >
                        <Dropdown.Toggle>
                            {locale('settings.add.block')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {blockOptions.map((block) => (
                                isBlockAvailableForUser(block.template) && (
                                    <MenuItem
                                        key={block.template}
                                        onClick={() => handleAddBlock(block.template)}
                                    >
                                        {locale(`settings.blocks.${block.template}`)}
                                    </MenuItem>
                                )))}
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
            </Row>

            <Row style={{ marginTop: '20px' }}>
                <Col xs={12}>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {blocksList?.map((block, index) => {
                                        const reorderable = !(block.template === BLOCK_PLAYER || (block.template === 'shop' && pageTemplate === 'shop'));
                                        const editable = !(block.template === BLOCK_PLAYER || (block.template === 'shop' && pageTemplate === 'shop'));

                                        return <Draggable
                                            key={block.id}
                                            draggableId={`${block.id || index}`}
                                            index={index}
                                            isDragDisabled={!reorderable}
                                        >
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={
                                                        provided.draggableProps.style
                                                    }
                                                >
                                                    <ListGroupItem
                                                        key={block.id}
                                                        style={{
                                                            padding: '4px 4px 4px 12px',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            height: '44px',
                                                        }}
                                                    >
                                                        <span style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                                                            {reorderable && <MoveIcon />}
                                                            &nbsp;{locale(`settings.blocks.${block.template}`)}
                                                        </span>
                                                        {(editable && isBlockAvailableForUser(block.template)) &&
                                                            <ButtonGroup>
                                                                <Button onClick={() => handleSelectEditableBlock(block)}>
                                                                    <Glyphicon glyph="edit" />
                                                                </Button>
                                                                <DeleteConfirmOverlay
                                                                    title={locale('settings.confirm.removeBlock')}
                                                                    buttonText="Удалить"
                                                                    onConfirm={() => handleDeleteBlock(block.id)}
                                                                >
                                                                    <Button bsStyle="danger"><Glyphicon glyph="trash" /></Button>
                                                                </DeleteConfirmOverlay>
                                                            </ButtonGroup>
                                                        }
                                                    </ListGroupItem>
                                                </div>
                                            )}
                                        </Draggable>
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Col>
            </Row>

            <BlockEditModal
                isModalVisible={isEditBlockModalVisible}
                editableBlock={editableBlock}
                updateBlock={updateBlock}
                template={editableBlock?.template}
                updatePageSettings={updatePageSettings}
                event={event}
                streams={event.stream}
                onCloseModal={handleCloseModal}
            />

            <Toaster position="top-right" />
        </>
    );
};

export default BlockSettings;
